import { useEffect, useRef, useState } from 'react';
import { prefiereQuieto } from '../utils/motion';

// Un solo IntersectionObserver para todos los Reveal de la página (más barato
// que uno por bloque). Cada elemento se libera al entrar: se anima una sola
// vez, volver a animar al subir y bajar marea.
let observador = null;
const callbacks = new WeakMap();

function observar(el, alEntrar) {
  if (!observador) {
    observador = new IntersectionObserver(
      (entradas) => entradas.forEach((e) => {
        if (!e.isIntersecting) return;
        observador.unobserve(e.target);
        const cb = callbacks.get(e.target);
        callbacks.delete(e.target);
        if (cb) cb();
      }),
      // rootMargin negativo abajo: dispara cuando el bloque ya entró de verdad,
      // no cuando asoma un pixel.
      { threshold: 0.05, rootMargin: '0px 0px -60px 0px' }
    );
  }
  callbacks.set(el, alEntrar);
  observador.observe(el);
  return () => { callbacks.delete(el); observador.unobserve(el); };
}

// Aparece al entrar en pantalla: opacidad + 16px de desplazamiento (solo
// transform/opacity, sin layout shift). El escalonado lo da `delay` y se usa
// una vez por sección. Con prefers-reduced-motion se muestra directo.
// Si el foco de teclado entra antes que el observer (Tab rápido en móvil), se
// muestra al instante y sin escalonado: el control enfocado nunca queda invisible.
export default function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(() => prefiereQuieto());
  const [porFoco, setPorFoco] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    // Sin IntersectionObserver (Safari viejo) se muestra directamente.
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    return observar(el, () => setVisible(true));
  }, [visible]);

  return (
    <div
      ref={ref}
      data-visible={visible}
      className={`lp-reveal ${className}`}
      onFocus={visible ? undefined : () => { setPorFoco(true); setVisible(true); }}
      style={delay ? { transitionDelay: visible && !porFoco ? `${delay}ms` : '0ms' } : undefined}
    >
      {children}
    </div>
  );
}
